import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import { Button, Form } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { showNotification } from "../../service/showNotification";
import { createAssetColumn, updateAssetColumn } from "../../service/api";
import { convertFormula, revertFormula } from "../../utils/assetColumnFormula";
import { assetColumnSchema } from "../../yup";
import styles from "./index.module.css";
import clsx from "clsx";
import {
  nonCriticalColumns,
  nonEditableFields,
} from "../AssetsTableV2/FieldsArray";
import { tracker } from "../../service/mixpanel";
// Individual cell components with React Hook Form integration
const TextInputCell = ({
  name,
  control,
  error,
  placeholder,
  disabled = false,
}) => (
  <td>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <input
          {...field}
          value={field.value || ""}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(styles.defaultInput, styles.inputField)}
          style={{
            border: error ? "1px solid red" : "1px solid #ccc",
            backgroundColor: disabled ? "#f8f9fa" : "white",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
      )}
    />
    {error && (
      <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
    )}
  </td>
);

const SelectCell = ({
  name,
  control,
  options,
  isLoading,
  error,
  disabled = false,
}) => (
  <td>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          value={options.find((opt) => opt.value === field.value) || null}
          onChange={(selected) => field.onChange(selected?.value)}
          options={options}
          isLoading={isLoading}
          isDisabled={disabled}
          styles={{
            control: (base) => ({
              ...base,
              width: "200px",
              minHeight: "32px",
              fontSize: "14px",
              borderColor: error ? "red" : "#ccc",
            }),
            menuList: (base) => ({ ...base, maxHeight: 120 }),
          }}
          menuPlacement="auto"
        />
      )}
    />
    {error && (
      <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
    )}
  </td>
);

const MultiSelectCell = ({
  name,
  control,
  options,
  isLoading,
  error,
  disabled = false,
}) => (
  <td>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          isMulti
          value={options.filter((opt) => field.value?.includes(opt.value))}
          onChange={(selected) =>
            field.onChange(selected.map((opt) => opt.value))
          }
          options={options}
          isLoading={isLoading}
          isDisabled={disabled}
          styles={{
            control: (base) => ({
              ...base,
              width: "430px",
              minHeight: "32px",
              fontSize: "14px",
              borderColor: error ? "red" : "#ccc",
            }),
            menuList: (base) => ({
              ...base,
              maxHeight: (field.value?.length ?? 0) > 2 ? 80 : 120,
            }),
          }}
          menuPlacement="auto"
        />
      )}
    />
    {error && (
      <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
    )}
  </td>
);

const CreatableSelectCell = ({ name, control, error, disabled = false }) => (
  <td>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CreatableSelect
          {...field}
          isMulti
          isDisabled={disabled}
          value={(field.value || []).map((label) => ({ label, value: label }))}
          onChange={(selected) => {
            const values = selected ? selected.map((item) => item.value) : [];
            field.onChange(values);
          }}
          onCreateOption={(inputValue) => {
            const cleanedValue = inputValue.trim();
            if (cleanedValue && !(field.value || []).includes(cleanedValue)) {
              field.onChange([...(field.value || []), cleanedValue]);
            }
          }}
          placeholder="Type labels..."
          styles={{
            control: (base) => ({
              ...base,
              minHeight: "32px",
              fontSize: "14px",
              borderColor: error ? "red" : "#ccc",
              flexWrap: "no-wrap",
            }),
          }}
          noOptionsMessage={() => "Enter CSV column for mapping."}
          formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
          menuPlacement="auto"
        />
      )}
    />
    {error && (
      <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
    )}
  </td>
);

const SwitchCell = ({ name, control, disabled = false }) => (
  <td style={{ textAlign: "center" }}>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Form.Check
          type="switch"
          disabled={disabled}
          checked={field.value || false}
          className={styles.roundedSwitch}
          onChange={(e) => field.onChange(e.target.checked)}
        />
      )}
    />
  </td>
);

// Enhanced Formula Input Cell with autocomplete
const FormulaInputCell = ({
  name,
  control,
  error,
  disabled,
  formulaOptions,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);

  const handleFormulaChange = (value, onChange) => {
    onChange(value);

    if (disabled) return;

    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart;
    const hashIndex = value.lastIndexOf("#", cursor - 1);
    const afterHash = value.slice(hashIndex + 1, cursor);

    if (hashIndex !== -1 && !/\s/.test(afterHash)) {
      const matches = formulaOptions?.filter((opt) =>
        opt.label.toLowerCase().includes(afterHash.toLowerCase())
      );
      setFilteredOptions(matches);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelect = (option, currentValue, onChange) => {
    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart;
    const hashIndex = currentValue.lastIndexOf("#", cursor - 1);
    const before = currentValue.slice(0, hashIndex);
    const after = currentValue.slice(cursor);
    const newValue = `${before}#${option.label} ${after}`;

    onChange(newValue);
    setShowDropdown(false);
    input.focus();
  };

  useEffect(() => {
    const handler = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  return (
    <td>
      <div style={{ position: "relative" }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <>
              <input
                {...field}
                ref={inputRef}
                value={field.value || ""}
                onChange={(e) =>
                  handleFormulaChange(e.target.value, field.onChange)
                }
                placeholder="Enter formula (type # to use column names)"
                disabled={disabled}
                className={clsx(styles.formulaInput, styles.inputField)}
                style={{
                  border: error ? "1px solid red" : "1px solid #ccc",
                  backgroundColor: disabled ? "#f8f9fa" : "white",
                  cursor: disabled ? "not-allowed" : "text",
                }}
                autoComplete="off"
              />

              {showDropdown && !disabled && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    maxHeight: "150px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  {filteredOptions.length === 0 ? (
                    <div style={{ padding: "8px", color: "#666" }}>
                      No matches
                    </div>
                  ) : (
                    filteredOptions.map((opt) => (
                      <div
                        key={opt.value}
                        style={{
                          padding: "8px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "white")
                        }
                        onClick={() =>
                          handleSelect(opt, field.value, field.onChange)
                        }
                      >
                        {opt.label}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        />
      </div>
      {error && (
        <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
      )}
    </td>
  );
};

const dataTypeOptions = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date" },
];

export const dateFormatOptions = [
  { label: "DD-MM-YYYY", value: "DD-MM-YYYY" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
  { label: "DD.MM.YYYY", value: "DD.MM.YYYY" },

  { label: "MM-DD-YYYY", value: "MM-DD-YYYY" },
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
  { label: "MM.DD.YYYY", value: "MM.DD.YYYY" },

  { label: "YYYY-MM-DD", value: "YYYY-MM-DD" },
  { label: "YYYY/MM/DD", value: "YYYY/MM/DD" },
  { label: "YYYY.MM.DD", value: "YYYY.MM.DD" },

  { label: "DD-MMM-YYYY", value: "DD-MMM-YYYY" },
  { label: "DD/MMM/YYYY", value: "DD/MMM/YYYY" },
  { label: "DD.MMM.YYYY", value: "DD.MMM.YYYY" },

  { label: "MMM-DD-YYYY", value: "MMM-DD-YYYY" },
  { label: "MMMM-DD-YYYY", value: "MMMM-DD-YYYY" },
  { label: "DD-MMMM-YYYY", value: "DD-MMMM-YYYY" },

  { label: "MMMM DD, YYYY", value: "MMMM DD, YYYY" },
  { label: "MMM DD, YYYY", value: "MMM DD, YYYY" },
  { label: "DD MMMM YYYY", value: "DD MMMM YYYY" },
];

export const allowedDateFormats = dateFormatOptions.map((item) => item.value);

// Main table row component with React Hook Form
const FormTableRow = ({
  rowData,
  zohoAssetOptions,
  columns,
  onDelete,
  fetchColumns,
}) => {
  const formulaOptions =
    columns
      ?.filter(
        (item) => "number" === item?.dataType && item._id !== rowData?._id
      )
      ?.map((item) => ({
        label: item?.label.replaceAll(" ", "_"),
        value: item?._id,
      })) || [];

  const isNonEditable = nonEditableFields.some(
    (field) => field.label == rowData?.label
  );
  const isZohoField = rowData?.isZohoField;
  const zohoLabelDisabled = rowData?.label == "Record Id";
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(assetColumnSchema),
    defaultValues: {
      label: rowData?.label || "",
      zohoLabel: rowData?.zohoLabel || "",
      csvLabel: rowData?.csvLabel || [],
      dataType: rowData?.dataType || "string",
      defaultValue: rowData?.defaultValue || "",
      isFormula: rowData?.isFormula || false,
      formula: revertFormula(rowData?.formula, formulaOptions),
      validation: {
        required: rowData?.validation?.required || false,
        dateFormat: rowData?.validation?.dateFormat,
      },
      zohoDateFormat: rowData?.zohoDateFormat,
      isCritical: !!rowData?.isCritical,
      acceptedFieldLocked: !!rowData?.acceptedFieldLocked,
    },
  });

  const isFormula = watch("isFormula");
  const dataType = watch("dataType");
  const isValidationRequired = watch("validation.required");
  const isDefaultValue = !!watch("defaultValue");
  const isCsvLabel = !!watch("csvLabel")?.length;

  const disableColumnName = isNonEditable || rowData?.isInternal || isZohoField;
  const disableCsvLabel = isDefaultValue || rowData?.isInternal || isZohoField;
  const disableZohoLabel =
    zohoLabelDisabled || rowData?.isInternal || isZohoField;
  const disableDataType = isNonEditable || rowData?.isInternal || isZohoField;
  const disableDefaultValue =
    isCsvLabel ||
    isFormula ||
    isValidationRequired ||
    isNonEditable ||
    rowData?.isInternal ||
    isZohoField;
  const disableIsFormula =
    isDefaultValue ||
    !("number" === dataType) ||
    isNonEditable ||
    rowData?.isInternal ||
    isZohoField;
  const disableFormula =
    !isFormula || isNonEditable || rowData?.isInternal || isZohoField;
  const disableValidationRequired =
    isDefaultValue ||
    isFormula ||
    isNonEditable ||
    rowData?.isInternal ||
    isZohoField;
  const disableValidationDateFormat =
    dataType !== "date" || isNonEditable || rowData?.isInternal || isZohoField;

  const disableIsCritical =
    isFormula ||
    isZohoField ||
    rowData?.isInternal ||
    nonCriticalColumns.includes(rowData?._id);
  
  const disableAcceptedFieldLocked =
    isFormula || isZohoField || rowData?.isInternal || !rowData?.isCritical;

  const onFormSubmit = async (values) => {
    const isNew = rowData?._id?.toLowerCase()?.includes("new-");
    tracker.assetV2Events.assetColumnSaveOrUpdateClick(isNew);

    const formulaObj = convertFormula(values?.formula, formulaOptions);
    if (formulaObj) {
      values.formula = formulaObj?.convertedFormula;
      values.dependencies = formulaObj?.dependencies;
    }
    try {
      const res = isNew
        ? await createAssetColumn(values)
        : await updateAssetColumn(rowData?._id, values);

      fetchColumns();
      showNotification(
        "success",
        res?.message ||
          (rowData?._id
            ? "Asset updated successfully."
            : "New asset column created.")
      );
    } catch (err) {
      console.log("err", err);
      showNotification(
        "error",
        err?.response?.data?.message ||
          `Failed to ${rowData?._id ? "update" : "create"} new column.`
      );
    }
  };

  useEffect(() => {
    if (["string", "boolean"].includes(dataType)) {
      setValue("isFormula", false);
      setValue("formula", undefined);
      setValue("validation.dateFormat", []);
    }
    if ("number" === dataType) {
      setValue("validation.dateFormat", []);
    }
  }, [dataType]);

  useEffect(() => {
    if (isFormula) {
      setValue("validation.required", false);
    } else {
      setValue("formula", undefined);
    }
  }, [isFormula]);

  return (
    <>
      <TextInputCell
        name="label"
        control={control}
        error={errors.label}
        placeholder="Column name"
        disabled={disableColumnName}
      />

      <CreatableSelectCell
        name="csvLabel"
        control={control}
        error={errors.csvLabel}
        disabled={disableCsvLabel}
      />

      <SelectCell
        name="zohoLabel"
        control={control}
        options={zohoAssetOptions}
        error={errors.zohoLabel}
        disabled={disableZohoLabel}
      />

      <SelectCell
        name="dataType"
        control={control}
        options={dataTypeOptions}
        error={errors.dataType}
        disabled={disableDataType}
      />

      <TextInputCell
        name="defaultValue"
        control={control}
        error={errors.defaultValue}
        placeholder="Default value"
        disabled={disableDefaultValue}
      />

      <SwitchCell
        name="isFormula"
        control={control}
        disabled={disableIsFormula}
      />

      <FormulaInputCell
        name="formula"
        control={control}
        error={errors.formula}
        disabled={disableFormula}
        formulaOptions={formulaOptions}
      />

      {/* Validation fields - conditional rendering based on data type */}
      <SwitchCell
        name="validation.required"
        control={control}
        disabled={disableValidationRequired}
      />

      <SwitchCell
        name="isCritical"
        control={control}
        disabled={disableIsCritical}
      />

      <SwitchCell
        name="acceptedFieldLocked"
        control={control}
        disabled={disableAcceptedFieldLocked}
      />

      <MultiSelectCell
        name="validation.dateFormat"
        control={control}
        options={dateFormatOptions}
        error={errors.dateFormat}
        disabled={disableValidationDateFormat}
      />

      <SelectCell
        name="zohoDateFormat"
        control={control}
        options={dateFormatOptions}
        error={errors.zohoDateFormat}
        disabled={disableValidationDateFormat}
      />


      <td className={styles.actionRow}>
        <Button
          type="button"
          onClick={handleSubmit(onFormSubmit)}
          disabled={isSubmitting || rowData?.isInternal || !rowData?.isEditable}
          variant="success"
          size="sm"
          style={{ marginRight: "8px" }}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>

        <Button
          type="button"
          onClick={() => onDelete(rowData._id)}
          size="sm"
          variant="danger"
          disabled={!rowData?.canDelete}
        >
          Delete
        </Button>
      </td>
    </>
  );
};

export {
  FormTableRow,
  TextInputCell,
  SelectCell,
  CreatableSelectCell,
  SwitchCell,
};
